import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Heart, Store, Radio, Video, ShoppingBag, 
    Share2, ExternalLink, Sparkles, MessageCircle, Clock 
} from 'lucide-react';
import { getProducts, getVerifiedSellers, resolveStorefrontImageUrl } from '../api/client';
import { isRTL, getTranslation as t } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function FollowingFeedPage() {
    const rtl = isRTL();
    const [loading, setLoading] = useState(true);
    const [followedSellers, setFollowedSellers] = useState([]);
    const [feedItems, setFeedItems] = useState([]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem('luxvono_followed_sellers');
            if (raw) {
                setFollowedSellers(JSON.parse(raw));
            }
        } catch (_) {}
    }, []);

    useEffect(() => {
        const loadFeed = async () => {
            try {
                setLoading(true);
                const [prodsRes, sellersRes] = await Promise.all([
                    getProducts(0, 24).catch(() => ({ data: [] })),
                    getVerifiedSellers().catch(() => [])
                ]);
                const pList = prodsRes?.data?.content || (Array.isArray(prodsRes?.data) ? prodsRes.data : []);
                const sellersList = Array.isArray(sellersRes) ? sellersRes : [];

                const generatedFeed = [];
                if (sellersList.length > 0 && pList.length > 0) {
                    sellersList.slice(0, 8).forEach((seller, idx) => {
                        const prod = pList[idx % pList.length];
                        const sName = rtl ? (seller.businessName || seller.storeName || `متجر ${seller.sellerId}`) : (seller.storeName || seller.businessName || `Store ${seller.sellerId}`);
                        generatedFeed.push({
                            id: `feed-${seller.sellerId || idx}-${prod?.id || idx}`,
                            sellerName: seller.fullName || seller.ownerName || sName,
                            storeName: sName,
                            storeSlug: seller.sellerId ? `@${seller.sellerId}` : (seller.slug || 'seller'),
                            avatar: resolveStorefrontImageUrl(seller.logoUrl || seller.avatarUrl || ''),
                            type: idx % 2 === 0 ? 'PRODUCT_DROP' : 'VIDEO_REEL',
                            time: rtl ? 'مؤخراً' : 'Recently',
                            caption: rtl 
                                ? `وصل حديثاً إلى معرضنا: ${prod?.nameAr || prod?.name || 'قطعة فاخرة'} بأعلى معايير الجودة والمطابقة.` 
                                : `Just added to our collection: ${prod?.nameEn || prod?.name || 'Luxury item'} with certified authenticity.`,
                            product: {
                                id: prod?.id,
                                name: (rtl ? (prod?.nameAr || prod?.name) : (prod?.nameEn || prod?.name)) || prod?.name || 'Product',
                                price: `${prod?.price || 0} SAR`,
                                image: prod?.primaryImageUrl || prod?.image || ''
                            }
                        });
                    });
                }

                setFeedItems(generatedFeed);
            } catch (err) {
                console.error('Failed to load following feed:', err);
            } finally {
                setLoading(false);
            }
        };

        loadFeed();
    }, [rtl]);

    return (
        <div style={{ minHeight: '100vh', background: 'var(--lux-bg, #0B0F19)', color: 'var(--text, #F8FAFC)', padding: '40px 0 80px 0' }}>
            <div className="container" style={{ maxWidth: '720px', margin: '0 auto', padding: '0 20px' }}>
                
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#ec4899', fontSize: '12px', fontWeight: '800', letterSpacing: '1px', marginBottom: '4px' }}>
                            <Heart size={14} fill="#ec4899" />
                            {rtl ? 'خلاصة المتابعة المباشرة' : 'FOLLOWING FEED'}
                        </span>
                        <h1 style={{ fontSize: '24px', fontWeight: '900', margin: 0, color: '#f8fafc' }}>
                            {rtl ? 'تحديثات المتاجر التي تتابعها' : 'Updates from Your Followed Maisons'}
                        </h1>
                    </div>

                    <Link
                        to="/stores"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1e293b', border: '1px solid #334155', color: '#cbd5e1', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', textDecoration: 'none' }}
                    >
                        <Store size={14} />
                        <span>{rtl ? 'استكشاف متاجر جديدة' : 'Explore More Stores'}</span>
                    </Link>
                </div>

                {/* Feed Stream */}
                {loading ? (
                    <div style={{ padding: '80px 0', textAlign: 'center' }}>
                        <Spinner />
                    </div>
                ) : feedItems.length === 0 ? (
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
                        <Heart size={48} style={{ opacity: 0.3, marginBottom: '14px' }} />
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#f8fafc' }}>
                            {rtl ? 'لم تقم بمتابعة أي متاجر بعد' : 'No Followed Stores Yet'}
                        </h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px' }}>
                            {rtl ? 'تابع متاجرك المفضلة لتصلك أحدث الإصدارات ومقاطع الفيديو الحصرية فور نزولها.' : 'Follow your favorite creators to see new product drops and live reels here.'}
                        </p>
                        <Link
                            to="/stores"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #d4af37 0%, #aa8214 100%)', color: '#0b0f19', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', textDecoration: 'none' }}
                        >
                            <span>{rtl ? 'تصفح دليل المتاجر' : 'Browse Stores Directory'}</span>
                        </Link>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {feedItems.map(item => (
                            <div
                                key={item.id}
                                style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', overflow: 'hidden' }}
                            >
                                {/* Post Author Header */}
                                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #334155' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <Link to={`/seller/${item.storeSlug}`}>
                                            <img src={item.avatar} alt="" style={{ width: '42px', height: '42px', borderRadius: '10px', objectFit: 'cover' }} />
                                        </Link>
                                        <div>
                                            <Link to={`/seller/${item.storeSlug}`} style={{ textDecoration: 'none', color: '#f8fafc', fontWeight: '700', fontSize: '14px', display: 'block' }}>
                                                {item.storeName}
                                            </Link>
                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                {item.sellerName} • {item.time}
                                            </span>
                                        </div>
                                    </div>

                                    <Link
                                        to={`/seller/${item.storeSlug}`}
                                        style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)', color: '#d4af37', padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', textDecoration: 'none' }}
                                    >
                                        {rtl ? 'زيارة المتجر' : 'Visit Store'}
                                    </Link>
                                </div>

                                {/* Post Caption */}
                                <div style={{ padding: '16px 20px', fontSize: '13px', lineHeight: '1.6', color: '#e2e8f0' }}>
                                    {item.caption}
                                </div>

                                {/* Attached Canonical Product Card */}
                                {item.product && (
                                    <div style={{ margin: '0 20px 20px 20px', background: '#0b0f19', border: '1px solid #334155', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: '#1e293b', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <ShoppingBag size={22} color="#d4af37" />
                                            </div>
                                            <div>
                                                <strong style={{ display: 'block', color: '#f8fafc', fontSize: '13px' }}>
                                                    {item.product.nameAr || item.product.name}
                                                </strong>
                                                <span style={{ color: '#d4af37', fontWeight: '800', fontSize: '13px' }}>
                                                    {item.product.price} {item.product.currency || 'SAR'}
                                                </span>
                                            </div>
                                        </div>

                                        <Link
                                            to={`/product/${item.product.id}`}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: 'linear-gradient(135deg, #d4af37 0%, #aa8214 100%)',
                                                color: '#0b0f19',
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                fontWeight: '800',
                                                fontSize: '12px',
                                                textDecoration: 'none'
                                            }}
                                        >
                                            <span>{rtl ? 'عرض وشراء المنتج' : 'View Product'}</span>
                                            <ExternalLink size={13} />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
